import { useEffect } from "react";
import type { CrewMember } from "@/mocks/admin/crew";

type Props = {
  open: boolean;
  onClose: () => void;
  members: CrewMember[];
  currentId: string;
  onSelect: (id: string) => void;
};

export default function CrewSwitcherSheet({ open, onClose, members, currentId, onSelect }: Props) {
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    if (open) document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  return (
    <div
      className={`absolute inset-0 z-50 transition-opacity ${
        open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      }`}
    >
      <div className="absolute inset-0 bg-ink-900/50 backdrop-blur-sm" onClick={onClose}></div>
      <div
        className={`absolute left-0 right-0 bottom-0 bg-white rounded-t-3xl max-h-[78%] flex flex-col transition-transform duration-300 ${
          open ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="pt-3 pb-1 flex justify-center">
          <div className="w-10 h-1 rounded-full bg-ink-200"></div>
        </div>
        <div className="px-5 pt-2 pb-3 flex items-center justify-between border-b border-ink-100">
          <div>
            <h3 className="font-display text-base font-bold text-ink-900 flex items-center gap-1.5">
              <i className="ri-shield-user-line text-ink-500"></i>
              Admin Preview
            </h3>
            <p className="text-[11px] text-ink-500">
              Pick a crew member to preview their app. Crew see only their own private link.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-cream-100 text-ink-500 cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>
        <div className="overflow-y-auto scrollbar-thin flex-1">
          <ul className="divide-y divide-ink-100">
            {members.map((m) => (
              <li key={m.id}>
                <button
                  onClick={() => {
                    onSelect(m.id);
                    onClose();
                  }}
                  className={`w-full flex items-center gap-3 px-5 py-3 text-left cursor-pointer hover:bg-cream-50 ${
                    currentId === m.id ? "bg-brand-50/60" : ""
                  }`}
                >
                  <div
                    className={`w-10 h-10 rounded-full ${m.color} text-white text-xs font-bold flex items-center justify-center shrink-0`}
                  >
                    {m.initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-ink-900 truncate">{m.name}</div>
                    <div className="text-[11px] text-ink-500">
                      {m.role} · ${m.hourlyRate}/h
                    </div>
                  </div>
                  {currentId === m.id ? (
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-brand-500 text-white">
                      <i className="ri-check-line text-sm"></i>
                    </span>
                  ) : (
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-400">
                      {m.status}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}